import Button from "@/components/atoms/Button/Button";
import GradingState from "@/components/organisms/Grading/GradingState";
import { Dialog } from "@/components/organisms/Modal/Dialog";
import RecordingGradingContents from "@/components/Modal/RecordingGradingContents";
import { useState, useMemo } from "react";
import { academyLearningScheduleApi } from 'services/api';

interface GradingFlashCardProps {
    type?: 'scoreCard' | 'gradingCard'
    activity?: any
    responseJson?: string
    currentItemIndex?: number
    recordingGradingResults?: Record<string, 'correct' | 'incorrect'>
    onRecordingGradingChange?: (itemId: number | string, status: 'correct' | 'incorrect' | undefined) => void
    canUseAi?: boolean
    lockedRecordingKeys?: Set<string>
}

const GradingFlashCard = ({ type = 'scoreCard', activity, responseJson, currentItemIndex, recordingGradingResults = {}, onRecordingGradingChange, canUseAi = true, lockedRecordingKeys }: GradingFlashCardProps) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [selectedWordIndex, setSelectedWordIndex] = useState<number | null>(null);
    const [currentDialogStatus, setCurrentDialogStatus] = useState<'correct' | 'incorrect'>('correct');
    const [aiLoadingSet, setAiLoadingSet] = useState<Set<number>>(new Set());

    // 실제 데이터를 파싱하여 사용
    const wordList = useMemo(() => {
        if (!activity || !activity.items) {
            // 더미 데이터 (데이터가 없을 때)
            return [
                { id: 1, word: 'Cat', status: 'correct' as const, recordingUrl: null },
                { id: 2, word: 'Dog', status: 'incorrect' as const, recordingUrl: null },
                { id: 3, word: 'Bird', status: 'grading' as const, recordingUrl: null },
                { id: 4, word: 'Fish', status: 'correct' as const, recordingUrl: null },
                { id: 5, word: 'Lion', status: 'incorrect' as const, recordingUrl: null }
            ];
        }

        // responseJson 파싱 (단일 아이템 응답 또는 아이템 맵)
        let responses: any = {};
        if (responseJson) {
            try {
                responses = JSON.parse(responseJson);
            } catch (e) {
                console.error('responseJson 파싱 실패:', e);
            }
        }

        // 실제 데이터 매핑
        return activity.items.map((item: any, index: number) => {
            let contentJson: any = {};
            try {
                contentJson = JSON.parse(item.contentJson || '{}');
            } catch (e) {
                console.error('contentJson 파싱 실패:', e);
            }

            // responses가 아이템 맵({"0": {...}})인지, 단일 아이템 응답({recordingUrl: ...})인지 판별
            let itemResponse: any = {};
            const isMapLike = responses && typeof responses === 'object' && Object.keys(responses).some(k => /^\d+$/.test(k));
            if (isMapLike) {
                // 맵 구조: index에 해당하는 응답 사용
                itemResponse = responses[String(index)] ?? Object.values(responses)[index] ?? {};
            } else {
                // 단일 응답 구조: 현재 플랫 아이템 인덱스에만 적용
                if (typeof currentItemIndex === 'number' && currentItemIndex === index) {
                    itemResponse = responses;
                }
            }
            
            // 상태 판단: 사용자가 채점했으면 그 결과를, 안했으면 '채점 필요'
            let status: 'correct' | 'incorrect' | 'grading' = 'grading';
            // activity.id와 index를 함께 사용하여 고유한 키 생성 (id 중복 방지)
            const itemIdStable = item?.id ?? index;
            const itemKey = `flashcard-${activity.id}-${index}`;
            if (recordingGradingResults[itemKey]) {
                // 수동 채점 결과가 있으면 그것을 사용 (정답 또는 오답)
                status = recordingGradingResults[itemKey];
            }
            // recordingGradingResults에 없으면 'grading' (채점 필요) 상태 유지

            return {
                id: itemIdStable,
                index,
                activityId: activity.id, // activity.id도 저장
                word: contentJson.word || contentJson.sentence || '단어',
                status: status,
                recordingUrl: (itemResponse && typeof itemResponse === 'object' ? itemResponse.recordingUrl : null) || null
            };
        });
    }, [activity, responseJson, recordingGradingResults, currentItemIndex]);

    const handleOpenDialog = (index: number) => {
        setSelectedWordIndex(index);
        // Dialog를 열 때 해당 단어의 현재 채점 상태를 설정
        if (wordList[index]) {
            const itemKey = `flashcard-${wordList[index].activityId}-${wordList[index].index}`;
            const currentStatus = recordingGradingResults[itemKey] || 'correct';
            setCurrentDialogStatus(currentStatus);
        }
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedWordIndex(null);
    };

    const handleConfirmGrading = () => {
        // 확인 버튼을 눌렀을 때 부모에게 알림
        if (selectedWordIndex !== null && wordList[selectedWordIndex]) {
            const word = wordList[selectedWordIndex];
            const uniqueKey = `${word.activityId}-${word.index}`; // index 기반 고유키
            console.log(`[GradingFlashCard] 녹음 채점 변경: activityId=${word.activityId}, index=${word.index}, uniqueKey=${uniqueKey}, status=${currentDialogStatus}`);
            onRecordingGradingChange?.(uniqueKey, currentDialogStatus);
        }
        handleCloseDialog();
    };

    const handleGradingChange = (status: 'correct' | 'incorrect') => {
        console.log(`[GradingFlashCard] 라디오 버튼 변경: status=${status}`);
        setCurrentDialogStatus(status);
    };

    const selectedWord = selectedWordIndex !== null ? wordList[selectedWordIndex] : null;
    const selectedWordKey = selectedWord ? `flashcard-${selectedWord.activityId}-${selectedWord.index}` : null;
    const isDialogLocked = selectedWordKey ? (lockedRecordingKeys?.has(selectedWordKey) ?? false) : false;
    const dialogReadOnly = type !== 'gradingCard' || isDialogLocked;

    // 현재 화면에 표시할 인덱스 집합 (플랫 아이템 형태라면 해당 인덱스만 표시)
    const indicesToShow: number[] = typeof currentItemIndex === 'number'
        ? (wordList[currentItemIndex] ? [currentItemIndex] : [])
        : wordList.map((_: { id: number | string; word: string; status: 'correct' | 'incorrect' | 'grading'; recordingUrl: string | null }, idx: number) => idx);

    return (
        <div className=''>
            <p className='mb-3 text-lg font-bold'>단어를 직접 녹음하고 들어보세요. (플래시 카드)</p>
            <div className='flex flex-col gap-2'>
                {indicesToShow.map((absIndex: number) => {
                    const wordItem = wordList[absIndex];
                    if (!wordItem) return null;
                    const itemKey = `flashcard-${wordItem.activityId}-${wordItem.index}`;
                    const isLocked = lockedRecordingKeys?.has(itemKey) ?? false;
                    const canGrade = type === 'gradingCard' && !isLocked;
                    const showViewButton = type === 'scoreCard' || isLocked;
                    return (
                      <div key={wordItem.id} className='flex justify-between items-center py-1'>
                          <p className='text-lg font-bold'>{wordItem.word}</p>
                          <div className='flex gap-2'>
                              {canGrade && (
                              <div className='flex gap-2 items-center flex-wrap'>
                                  {canUseAi && (
                                    <Button size='small' onClick={async () => {
                                      try {
                                        const w = wordItem;
                                        // 녹음이 없으면 백엔드에서 오답 처리하므로 그대로 요청
                                        const scheduleId = (window as any).__CURRENT_SCHEDULE_ID__;
                                        setAiLoadingSet(prev => { const s = new Set(prev); s.add(absIndex); return s });
                                        const uniqueKey = `${w.activityId}-${w.index}`;
                                        if (lockedRecordingKeys?.has(`flashcard-${uniqueKey}`)) {
                                          setAiLoadingSet(prev => { const s = new Set(prev); s.delete(absIndex); return s });
                                          return;
                                        }
                                        // 채점 중 표시를 위해 상위 상태를 일시적으로 undefined로
                                        onRecordingGradingChange?.(uniqueKey, undefined);
                                        const res = await academyLearningScheduleApi.aiGradeDictation(scheduleId, {
                                          recordingUrl: w.recordingUrl || '',
                                          correctWord: String(w.word || '').toString(),
                                        });
                                        const ok = !!res?.data?.correct;
                                        onRecordingGradingChange?.(uniqueKey, ok ? 'correct' : 'incorrect');
                                      } catch (e) {
                                        console.error('FLASH_CARD AI 채점 실패', e);
                                        alert('AI 채점에 실패했습니다.');
                                      } finally {
                                        setAiLoadingSet(prev => { const s = new Set(prev); s.delete(absIndex); return s });
                                      }
                                  }} disabled={aiLoadingSet.has(absIndex)}>{aiLoadingSet.has(absIndex) ? '채점중...' : 'AI 채점 하기'}</Button>
                                  )}
                                  <Button size='small' intent='secondary' onClick={() => handleOpenDialog(absIndex)}>녹음 듣고 채점하기</Button>
                              </div>
                              )}
                              {showViewButton && (
                                  <Button size='small' intent='secondary' onClick={() => handleOpenDialog(absIndex)}>녹음 듣기</Button>
                              )}
                              <div className='w-[70px] flex-none flex items-center justify-center'>
                                  <GradingState status={wordItem.status} />
                              </div>  
                          </div>
                      </div>
                    );
                })}
            </div>
            {/* 개별 단어 채점 Dialog */}
            <Dialog
                open={isDialogOpen}
                onCancel={handleCloseDialog}
                onOk={dialogReadOnly ? handleCloseDialog : handleConfirmGrading}
                okText={dialogReadOnly ? '닫기' : '확인'}
                cancelText={dialogReadOnly ? undefined : '취소'}
            >
                {selectedWord && (
                    <RecordingGradingContents 
                        word={selectedWord.word}
                        recordingUrl={selectedWord.recordingUrl}
                        initialGradingStatus={currentDialogStatus}
                        onGradingChange={dialogReadOnly ? undefined : handleGradingChange}
                        readOnly={dialogReadOnly}
                    />
                )}
            </Dialog>
        </div>
    )
}

export default GradingFlashCard;